import { useEffect, useState } from "react";
import { Loader2, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Label } from "@/components/ui-kit/Input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";

const ta = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40";

/**
 * Mark an outstanding invoice as written off (report-only flag for year-end tax
 * prep). Never alters the frozen invoice amount or status. `invoice` needs at
 * least { id, number, balance_due }.
 */
export function WriteOffDialog({ open, onOpenChange, invoice, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setReason(invoice?.write_off_reason || ""); setError(""); }
  }, [open, invoice]);

  const submit = async () => {
    setBusy(true); setError("");
    const id = invoice?.id ?? invoice?.invoice_id;
    try {
      await api.post(`/invoices/${id}/write-off`, { written_off: true, reason: reason.trim() || undefined });
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="write-off-dialog">
        <DialogTitle className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-status-void/10 text-status-void"><Ban className="h-4 w-4" /></span>
          Write off invoice {invoice?.number}
        </DialogTitle>
        <DialogDescription>
          Marks the {money(invoice?.balance_due)} outstanding balance as uncollectible for the
          year-end report. This does not change the invoice amount, its status, or any payment —
          it is a reporting flag only.
        </DialogDescription>
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="wo-reason">Reason (optional)</Label>
          <textarea id="wo-reason" data-testid="write-off-reason" rows={3} className={ta}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Client uncollectible, balance forgiven" />
        </div>
        {error && <p className="mt-2 text-sm text-destructive" data-testid="write-off-error">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="write-off-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="write-off-confirm"
            className="bg-status-void text-white hover:bg-status-void/90 focus-visible:ring-status-void/40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Mark written off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
